import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { BarChart2, ChevronDown, Download, X } from 'lucide-react';
import { MultiSelectDropdown, useNormativeFeatures, usePatientZScores } from '../../NormativeModeling';
import { apiFetch } from '../../api';
import { formatFeatureLabel } from '../../featureLabels';
import { CLINICIAN_SOZ_FEATURES } from '../constants';
import { buildClinicalReportHtml } from '../lib/patientReport';
import { PatientSummaryCards } from './PatientSummaryCards';


// -------------------------------
// Subcomponent: AbnormalitiesPanel
// Contains IEEG Abnormalities controls including Z-Score Visualization
// -------------------------------
export const AbnormalitiesPanel = ({
    selectedCenters,
    selectedPatients,
    onCenterToggle,
    onPatientToggle,
    centers,
    patients,
    centersLoading,
    centersError,
    patientsLoading,
    patientsError,
    loading,
    patientDataApi,
    brainCaptureApi,
    onDeselectAllPatients
}) => {
    const { features, loading: featuresLoading } = useNormativeFeatures();
    const { zScoreVisualizationSettings, setZScoreVisualizationSettings, patientZScores, setPatientZScores, montageType } = usePatientZScores();
    const [selectedFeatures, setSelectedFeatures] = useState([]);
    const [zScoresLoading, setZScoresLoading] = useState(false);
    const [clinicalOverlayData, setClinicalOverlayData] = useState(null);
    const [patientSummaries, setPatientSummaries] = useState([]);
    const [patientSummariesLoading, setPatientSummariesLoading] = useState(false);
    const [patientSummariesError, setPatientSummariesError] = useState(null);
    const [patientSummaryOpen, setPatientSummaryOpen] = useState(false);
    const zScoreRequestSignatureRef = useRef(null);


    // Extract updatePatientVisualization from patientDataApi
    const { updatePatientVisualization, clearAllPatientData } = patientDataApi || {};
    const availableFeatureIds = useMemo(() => new Set(features.map(feature => feature.id)), [features]);
    const selectedClinicianFeature = useMemo(() => (
        CLINICIAN_SOZ_FEATURES.find(feature => feature.id === zScoreVisualizationSettings.feature) || null
    ), [zScoreVisualizationSettings.feature]);

    const patientNormativeFeatureIds = useMemo(() => {
        const sozFeatureIds = CLINICIAN_SOZ_FEATURES
            .map(feature => feature.id)
            .filter(featureId => availableFeatureIds.has(featureId));
        const ids = new Set(sozFeatureIds);
        if (zScoreVisualizationSettings.feature) {
            ids.add(zScoreVisualizationSettings.feature);
        }
        if (ids.size === 0 && selectedFeatures.length > 0) {
            ids.add(selectedFeatures[0]);
        }
        return Array.from(ids);
    }, [availableFeatureIds, selectedFeatures, zScoreVisualizationSettings.feature]);

    const fetchClinicalOverlay = useCallback(async () => {
        if (!zScoreVisualizationSettings.showClinicalOverlay || selectedPatients.length === 0) {
            setClinicalOverlayData(null);
            return null;
        }

        const patientIdsParam = selectedPatients.join(',');
        const response = await apiFetch(`/api/clinical-overlays/patient?patient_ids=${encodeURIComponent(patientIdsParam)}`);
        if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.message || errorData.error || `HTTP error! Status: ${response.status}`);
        }

        const data = await response.json();
        setClinicalOverlayData(data);
        return data;
    }, [selectedPatients, zScoreVisualizationSettings.showClinicalOverlay]);

    useEffect(() => {
        if (selectedPatients.length === 0) {
            setPatientSummaries([]);
            setPatientSummariesError(null);
            return;
        }

        let cancelled = false;
        const fetchPatientSummaries = async () => {
            setPatientSummariesLoading(true);
            setPatientSummariesError(null);
            try {
                const response = await apiFetch(`/api/patient-summary?patient_ids=${encodeURIComponent(selectedPatients.join(','))}`);
                if (!response.ok) {
                    const errorData = await response.json();
                    throw new Error(errorData.message || errorData.error || `HTTP error! Status: ${response.status}`);
                }
                const data = await response.json();
                if (!cancelled) {
                    setPatientSummaries(data.patients || []);
                }
            } catch (error) {
                if (!cancelled) {
                    setPatientSummaries([]);
                    setPatientSummariesError(error.message || 'Failed to load patient metadata');
                }
            } finally {
                if (!cancelled) {
                    setPatientSummariesLoading(false);
                }
            }
        };

        fetchPatientSummaries();
        return () => {
            cancelled = true;
        };
    }, [selectedPatients]);

    // If no features are selected initially, select all features
    useEffect(() => {
        if (!featuresLoading && features.length > 0 && selectedFeatures.length === 0) {
            setSelectedFeatures(features.map(feature => feature.id));
        }
    }, [featuresLoading, features, selectedFeatures]);

    // Auto-pick a default feature only on the first load where features are available
    // and none is set yet. The ref is armed the moment features finish loading — even
    // when a feature was already restored from persistence and no auto-pick happens —
    // so this never re-fires after a settings reset clears the feature. That lets reset
    // leave the "All Features" dropdown on "Select a feature" instead of snapping back.
    const didInitFeatureRef = useRef(false);
    useEffect(() => {
        if (featuresLoading || features.length === 0 || didInitFeatureRef.current) {
            return;
        }
        didInitFeatureRef.current = true;
        if (!zScoreVisualizationSettings.feature) {
            setZScoreVisualizationSettings(prev => ({
                ...prev,
                enabled: true,
                feature: features[0].id
            }));
        }
    }, [featuresLoading, features, zScoreVisualizationSettings.feature, setZScoreVisualizationSettings]);

    useEffect(() => {
        zScoreRequestSignatureRef.current = null;
        setPatientZScores([]);
    }, [montageType, setPatientZScores]);

    useEffect(() => {
        if (selectedPatients.length === 0) {
            zScoreRequestSignatureRef.current = null;
            setPatientZScores([]);
            setClinicalOverlayData(null);
        }
    }, [selectedPatients.length, setPatientZScores]);

    const handleZScoreFeatureChange = useCallback((featureId) => {
        setZScoreVisualizationSettings(prev => ({
            ...prev,
            feature: featureId
        }));
        zScoreRequestSignatureRef.current = null;
        setPatientZScores([]);
        setClinicalOverlayData(null);
        clearAllPatientData?.();
    }, [setZScoreVisualizationSettings, setPatientZScores, clearAllPatientData]);

    const handleGenerateZScores = useCallback(async ({ showButtonLoading = true, force = true } = {}) => {
        if (selectedPatients.length === 0) {
            alert('Please select at least one patient');
            return;
        }
        if (!zScoreVisualizationSettings.feature) {
            return;
        }

        try {
            const patientIdsParam = selectedPatients.join(',');
            const featureIds = patientNormativeFeatureIds.length > 0
                ? patientNormativeFeatureIds
                : [zScoreVisualizationSettings.feature];
            const featureIdsParam = featureIds.join(',');
            const requestSignature = JSON.stringify({
                patients: [...selectedPatients].sort(),
                features: [...featureIds].sort(),
                montageType
            });

            if (!force && zScoreRequestSignatureRef.current === requestSignature) {
                return;
            }

            zScoreRequestSignatureRef.current = requestSignature;
            if (showButtonLoading) {
                setZScoresLoading(true);
            }

            // First, check if the selected patients have electrode data in the features table
            const checkElectrodeDataPromises = selectedPatients.map(async (patientId) => {
                try {
                    const response = await apiFetch(`/api/check-patient-electrodes?patient_id=${encodeURIComponent(patientId)}`);
                    if (response.ok) {
                        const data = await response.json();
                        return { patientId, hasElectrodes: data.hasElectrodes, count: data.count };
                    }
                    return { patientId, hasElectrodes: false, count: 0 };
                } catch (e) {
                    console.warn(`Failed to check electrodes for patient ${patientId}:`, e);
                    return { patientId, hasElectrodes: false, count: 0 };
                }
            });

            // Try to check electrode data, but continue even if this fails
            try {
                const electrodeResults = await Promise.all(checkElectrodeDataPromises);
                const patientsWithoutElectrodes = electrodeResults
                    .filter(result => !result.hasElectrodes)
                    .map(result => result.patientId);

                if (patientsWithoutElectrodes.length > 0) {
                    console.warn(`Patients without electrode data: ${patientsWithoutElectrodes.join(', ')}`);
                }

                const allPatientsWithoutElectrodes = patientsWithoutElectrodes.length === selectedPatients.length;
                if (allPatientsWithoutElectrodes) {
                    console.warn('None of the selected patients have electrode data');
                    // Continue anyway, the API will return empty results
                }
            } catch (e) {
                console.warn('Failed to check electrode data:', e);
                // Continue anyway
            }

            console.log("Requesting z-scores for patients:", selectedPatients);
            console.log("Requesting z-scores for features:", featureIds);

            // Call the API endpoint
            const response = await apiFetch(`/api/patient-z-scores?patient_ids=${encodeURIComponent(patientIdsParam)}&feature_ids=${encodeURIComponent(featureIdsParam)}&montage=${encodeURIComponent(montageType)}`);

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || `HTTP error! Status: ${response.status}`);
            }

            const data = await response.json();
            console.log("Received patient z-scores data:", data);
            // Refresh the clinical overlay; the authoritative plot effect re-renders
            // with the new z-scores + overlay once these state updates land.
            await fetchClinicalOverlay();

            // Set data in context — this drives the plot effect to recolor in place.
            setPatientZScores(data);

            // Check if data has regions
            if (data.length > 0) {
                console.log("First patient data:", data[0]);
                if (data[0].regions && data[0].regions.length > 0) {
                    console.log("First region:", data[0].regions[0]);
                    console.log(`✓ Successfully loaded z-scores for ${data.length} patient(s)`);
                } else {
                    console.warn("No regions in patient data");
                }
            } else {
                console.warn("Empty patient data array - no z-scores returned from backend");
            }
        } catch (error) {
            console.error('Error generating z-scores:', error);
        } finally {
            if (showButtonLoading) {
                setZScoresLoading(false);
            }
        }
    }, [selectedPatients, patientNormativeFeatureIds, setPatientZScores, zScoreVisualizationSettings, fetchClinicalOverlay, montageType]);

    useEffect(() => {
        if (
            selectedPatients.length === 0 ||
            !zScoreVisualizationSettings.feature ||
            patientNormativeFeatureIds.length === 0
        ) {
            return undefined;
        }

        const timer = window.setTimeout(() => {
            handleGenerateZScores({ showButtonLoading: false, force: false });
        }, 450);

        return () => window.clearTimeout(timer);
    }, [
        selectedPatients,
        zScoreVisualizationSettings.feature,
        patientNormativeFeatureIds,
        montageType,
        handleGenerateZScores
    ]);

    // Authoritative, immediate sync of the plotted scene to the current selection.
    // This is the single driver of patient plotting on the IEEG Abnormalities tab:
    // it runs synchronously whenever the selection (or the data that styles it)
    // changes, so electrodes appear/disappear immediately — no debounce lag — and a
    // rapid select+deselect cannot leave orphaned points. updatePatientVisualization
    // reconciles the scene to exactly `selectedPatients`, clearing it when empty.
    // Z-scores and clinical overlays are fetched separately (debounced, below) and
    // simply recolor the points in place once those state updates land.
    useEffect(() => {
        if (!updatePatientVisualization) {
            return;
        }
        updatePatientVisualization(
            selectedPatients,
            zScoreVisualizationSettings,
            patientZScores,
            clinicalOverlayData
        );
    }, [selectedPatients, zScoreVisualizationSettings, patientZScores, clinicalOverlayData, updatePatientVisualization]);

    useEffect(() => {
        if (!zScoreVisualizationSettings.showClinicalOverlay) {
            setClinicalOverlayData(null);
            return;
        }
        if (selectedPatients.length === 0) {
            setClinicalOverlayData(null);
            return;
        }
        fetchClinicalOverlay().catch(error => {
            console.warn('Failed to load clinical overlay metadata:', error);
        });
    }, [zScoreVisualizationSettings.showClinicalOverlay, selectedPatients, fetchClinicalOverlay]);

    const getPointColorClass = (zScore) => {
        const numericZ = Number(zScore);
        if (!Number.isFinite(numericZ)) {
            return 'unscored';
        }
        if (numericZ < 0) {
            return 'blue_negative_z';
        }
        if (numericZ > 0) {
            return 'red_positive_z';
        }
        return 'neutral_zero_z';
    };

    const flattenZScorePoints = (patientsPayload, featureName) => {
        return patientsPayload.flatMap(patient =>
            (patient.regions || []).flatMap(region =>
                (region.channels || []).map(channel => {
                    const featureData = channel.z_scores?.[featureName] || {};
                    const coords = channel.coordinates || {};
                    return {
                        patient_id: patient.patient_id,
                        region_name: region.region_name,
                        channel: channel.channel,
                        roi: channel.roi,
                        feature: featureName,
                        raw_value: featureData.raw_value ?? null,
                        z_score: featureData.z_score ?? null,
                        passes_threshold: Number.isFinite(Number(featureData.z_score))
                            ? Math.abs(Number(featureData.z_score)) >= Number(zScoreVisualizationSettings.threshold ?? 0)
                            : false,
                        color_class: getPointColorClass(featureData.z_score),
                        clinical_overlay: clinicalOverlayData?.patients
                            ?.find(overlayPatient => overlayPatient.patient_id === patient.patient_id)
                            ?.contacts
                            ?.find(contact => contact.contact === channel.channel || channel.channel?.includes(contact.contact)) || null,
                        mm_x: coords.mm_x ?? null,
                        mm_y: coords.mm_y ?? null,
                        mm_z: coords.mm_z ?? null,
                        surfmm_x: coords.surfmm_x ?? null,
                        surfmm_y: coords.surfmm_y ?? null,
                        surfmm_z: coords.surfmm_z ?? null,
                        normative_mean: featureData.normative_model?.mean ?? null,
                        normative_std: featureData.normative_model?.std ?? null
                    };
                })
            )
        );
    };

    const clinicalRegionSummaries = useMemo(() => (
        (clinicalOverlayData?.patients || []).flatMap(patient =>
            (patient.regions || []).map(region => ({
                patient_id: patient.patient_id,
                center: patient.center,
                roi: region.roi,
                region_name: region.region_name || region.roi || 'Unknown region',
                lobe: region.lobe,
                contact_count: region.contact_count || 0,
                is_soz: Boolean(region.is_soz || region.label_counts?.seizure_onset > 0),
                is_early_spread: Boolean(region.is_early_spread || region.label_counts?.early_spread > 0),
                is_spikes: Boolean(region.is_spikes || region.label_counts?.interictal_spikes > 0),
                is_lesion: Boolean(region.is_lesion || region.label_counts?.brain_lesions > 0),
                is_epileptic: Boolean(region.is_epileptic || region.label_counts?.epileptic > 0),
                label_counts: region.label_counts || {}
            }))
        )
    ), [clinicalOverlayData]);

    const handleDownloadSelections = async () => {
        if (selectedPatients.length === 0 || selectedFeatures.length === 0) {
            alert('Select at least one patient and one feature before downloading.');
            return;
        }

        try {
            const featureName = zScoreVisualizationSettings.feature || selectedFeatures[0];
            const patientIdsParam = selectedPatients.join(',');
            const response = await apiFetch(`/api/patient-z-scores?patient_ids=${encodeURIComponent(patientIdsParam)}&feature_ids=${encodeURIComponent(featureName)}&montage=${encodeURIComponent(montageType)}`);

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || errorData.error || `HTTP error! Status: ${response.status}`);
            }

            const zScorePayload = await response.json();
            const points = flattenZScorePoints(zScorePayload, featureName);
            const payload = {
                generated_at: new Date().toISOString(),
                patients: selectedPatients,
                centers: selectedCenters,
                feature: featureName,
                z_score_visualization: zScoreVisualizationSettings,
                clinical_overlay: clinicalOverlayData,
                clinical_region_summary: clinicalRegionSummaries,
                point_count: points.length,
                points
            };

            const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `ieeg-z-score-points-${featureName}-${Date.now()}.json`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Failed to download IEEG z-score points:', error);
            alert(`Unable to download z-score points: ${error.message || 'Please try again.'}`);
        }
    };

    const handleDownloadClinicalReport = async () => {
        if (selectedPatients.length === 0) {
            alert('Select at least one patient before creating a clinical report.');
            return;
        }
        if (!zScoreVisualizationSettings.feature) {
            alert('Select a z-score feature before creating a clinical report.');
            return;
        }

        try {
            const featureName = zScoreVisualizationSettings.feature;
            const patientIdsParam = selectedPatients.join(',');
            const response = await apiFetch(`/api/patient-z-scores?patient_ids=${encodeURIComponent(patientIdsParam)}&feature_ids=${encodeURIComponent(featureName)}&montage=${encodeURIComponent(montageType)}`);

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || errorData.error || `HTTP error! Status: ${response.status}`);
            }

            const zScorePayload = await response.json();
            let clinicalData = clinicalOverlayData;
            if (!clinicalData && selectedPatients.length > 0) {
                try {
                    clinicalData = await fetchClinicalOverlay();
                } catch (error) {
                    console.warn('Clinical overlay unavailable for report:', error);
                }
            }

            const points = flattenZScorePoints(zScorePayload, featureName);
            const threshold = Number(zScoreVisualizationSettings.threshold ?? 2);
            const reportThresholdedChannels = points
                .filter(point => Number.isFinite(Number(point.z_score)) && Math.abs(Number(point.z_score)) >= threshold)
                .map(point => ({
                    ...point,
                    z_score: Number(point.z_score),
                    abs_z_score: Math.abs(Number(point.z_score)),
                    coordinates: {
                        surfmm_x: point.surfmm_x,
                        surfmm_y: point.surfmm_y,
                        surfmm_z: point.surfmm_z
                    }
                }))
                .sort((a, b) => b.abs_z_score - a.abs_z_score);
            const reportClinicalRegions = (clinicalData?.patients || []).flatMap(patient =>
                (patient.regions || []).map(region => ({
                    patient_id: patient.patient_id,
                    center: patient.center,
                    roi: region.roi,
                    region_name: region.region_name || region.roi || 'Unknown region',
                    lobe: region.lobe,
                    contact_count: region.contact_count || 0,
                    is_soz: Boolean(region.is_soz || region.label_counts?.seizure_onset > 0),
                    is_early_spread: Boolean(region.is_early_spread || region.label_counts?.early_spread > 0),
                    is_spikes: Boolean(region.is_spikes || region.label_counts?.interictal_spikes > 0),
                    is_lesion: Boolean(region.is_lesion || region.label_counts?.brain_lesions > 0),
                    is_epileptic: Boolean(region.is_epileptic || region.label_counts?.epileptic > 0),
                    label_counts: region.label_counts || {}
                }))
            );
            const reportClinicalContacts = (clinicalData?.patients || []).flatMap(patient =>
                (patient.contacts || []).map(contact => ({
                    patient_id: patient.patient_id,
                    center: patient.center,
                    contact: contact.contact,
                    roi: contact.roi,
                    region_name: contact.region_name || contact.roi || 'Unknown region',
                    primary_label: contact.primary_label,
                    primary_label_display: contact.primary_label_display,
                    labels: contact.labels || [],
                    coordinates: contact.coordinates || {}
                }))
            );

            const html = buildClinicalReportHtml({
                generatedAt: new Date().toLocaleString(),
                patients: selectedPatients,
                centers: selectedCenters,
                feature: featureName,
                featureLabel: formatFeatureLabel(featureName),
                threshold,
                brainImage: brainCaptureApi?.captureImage?.() || null,
                patientSummaries,
                clinicalRegions: reportClinicalRegions,
                clinicalContacts: reportClinicalContacts,
                thresholdedChannels: reportThresholdedChannels,
                zScorePoints: points,
                zScoreSettings: zScoreVisualizationSettings
            });

            const blob = new Blob([html], { type: 'text/html' });
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `neuronova-clinical-report-${featureName}-${Date.now()}.html`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Failed to create clinical report:', error);
            alert(`Unable to create clinical report: ${error.message || 'Please try again.'}`);
        }
    };

    // Get all patients as a flat list, but with site info for grouping
    const patientsWithSiteInfo = useMemo(() => {
        return patients.map(patient => {
            const center = centers.find(c => c.patients && c.patients.includes(patient.id));
            return {
                ...patient,
                siteId: center?.id || 'unknown',
                siteName: center?.label || 'Unknown Site'
            };
        });
    }, [patients, centers]);

    // How many selected patients fall under each site, for the dropdown group labels.
    const selectedCountBySite = useMemo(() => {
        const selectedSet = new Set(selectedPatients);
        const counts = {};
        patientsWithSiteInfo.forEach(option => {
            if (selectedSet.has(option.id)) {
                const key = option.siteId || 'unknown';
                counts[key] = (counts[key] || 0) + 1;
            }
        });
        return counts;
    }, [patientsWithSiteInfo, selectedPatients]);

    return (
        <>
            <section className="control-section">
                <h3>Select Patients</h3>
                <MultiSelectDropdown
                    label="Patients"
                    options={patientsWithSiteInfo}
                    selectedItems={selectedPatients}
                    onToggle={onPatientToggle}
                    loading={patientsLoading}
                    error={patientsError}
                    groupBy={true}
                    getGroupKey={(option) => option.siteId || 'unknown'}
                    getGroupLabel={(groupKey) => {
                        const center = centers.find(c => c.id === groupKey);
                        const base = center?.label || groupKey;
                        const count = selectedCountBySite[groupKey] || 0;
                        return count > 0 ? `${base} [${count}]` : base;
                    }}
                    emptyMessage="No patients available"
                    collapsibleGroups={true}
                />
                {selectedPatients.length > 0 && (
                    <div className="reset-controls-row">
                        <button
                            type="button"
                            className="reset-control-button reset-control-button--danger"
                            onClick={onDeselectAllPatients}
                            title="Clear all selected patients and remove their plotted points"
                        >
                            <X size={14} />
                            <span>Deselect all patients</span>
                        </button>
                    </div>
                )}
            </section>

            {selectedPatients.length > 0 && (
            <section className="control-section">
                <div className="patient-summary-dropdown">
                    <div
                        className="patient-summary-header"
                        onClick={() => setPatientSummaryOpen(prev => !prev)}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                setPatientSummaryOpen(prev => !prev);
                            }
                        }}
                        aria-expanded={patientSummaryOpen}
                    >
                        <h3>Patient Summary{patientSummaries.length ? ` (${patientSummaries.length})` : ''}</h3>
                        <ChevronDown
                            size={18}
                            style={{
                                transform: patientSummaryOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                                transition: 'transform 0.2s ease'
                            }}
                        />
                    </div>
                    {patientSummaryOpen && (
                        <div className="patient-summary-dropdown-body">
                            <PatientSummaryCards
                                summaries={patientSummaries}
                                loading={patientSummariesLoading}
                                error={patientSummariesError}
                            />
                        </div>
                    )}
                </div>
            </section>
            )}

            <section className="control-section">
                <h3>Z-Score Visualization</h3>
                <div className="z-score-visualization-controls">
                    <div className="control-row">
                        <label htmlFor="clinician-soz-feature">Clinician SOZ Feature Preset:</label>
                        <select
                            id="clinician-soz-feature"
                            value={selectedClinicianFeature?.id || ''}
                            onChange={(e) => {
                                handleZScoreFeatureChange(e.target.value);
                                // Picking the placeholder clears the feature (like the
                                // All Features dropdown); only force the color/size
                                // presets when an actual SOZ feature is chosen.
                                if (e.target.value) {
                                    setZScoreVisualizationSettings(prev => ({
                                        ...prev,
                                        colorByDirection: true,
                                        scaleByMagnitude: true
                                    }));
                                }
                            }}
                            disabled={featuresLoading || features.length === 0}
                        >
                            <option value="">Select a SOZ feature</option>
                            {CLINICIAN_SOZ_FEATURES.map(feature => (
                                <option
                                    key={feature.id}
                                    value={feature.id}
                                    disabled={!availableFeatureIds.has(feature.id)}
                                    title={feature.rationale}
                                >
                                    {formatFeatureLabel(feature.id)}
                                </option>
                            ))}
                        </select>
                        {selectedClinicianFeature && (
                            <div className="clinical-feature-rationale">
                                <div className="clinical-feature-rationale-header">
                                    <span>{selectedClinicianFeature.category}</span>
                                </div>
                                <p>{selectedClinicianFeature.rationale}</p>
                            </div>
                        )}
                    </div>

                    <div className="control-row">
                        <label htmlFor="visualization-feature">All Features:</label>
                        <select
                            id="visualization-feature"
                            value={zScoreVisualizationSettings.feature}
                            onChange={(e) => handleZScoreFeatureChange(e.target.value)}
                            disabled={featuresLoading || features.length === 0}
                        >
                            <option value="">Select a feature</option>
                            {features.map(feature => (
                                <option key={feature.id} value={feature.id} title={feature.description}>
                                    {feature.label || feature.id}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div className="control-row">
                        <label className="checkbox-label">
                            <input
                                type="checkbox"
                                checked={zScoreVisualizationSettings.colorByDirection}
                                disabled={!zScoreVisualizationSettings.feature}
                                onChange={(e) => setZScoreVisualizationSettings(prev => ({
                                    ...prev,
                                    colorByDirection: e.target.checked
                                }))}
                            />
                            <span>Color by Z-Score Direction (red=positive, blue=negative)</span>
                        </label>
                    </div>

                    <div className="control-row">
                        <label className="checkbox-label">
                            <input
                                type="checkbox"
                                checked={zScoreVisualizationSettings.scaleByMagnitude}
                                disabled={!zScoreVisualizationSettings.feature}
                                onChange={(e) => setZScoreVisualizationSettings(prev => ({
                                    ...prev,
                                    scaleByMagnitude: e.target.checked
                                }))}
                            />
                            <span>Scale electrode size by Z-Score magnitude</span>
                        </label>
                    </div>

                    <div className="control-row">
                        <label className="checkbox-label">
                            <input
                                type="checkbox"
                                checked={Boolean(zScoreVisualizationSettings.thresholdMode)}
                                disabled={!zScoreVisualizationSettings.feature}
                                onChange={(e) => setZScoreVisualizationSettings(prev => ({
                                    ...prev,
                                    thresholdMode: e.target.checked
                                }))}
                            />
                            <span>Only show channels above threshold</span>
                        </label>
                        <div className="z-threshold-control">
                            <div className="z-threshold-header">
                                <span>|Z| threshold</span>
                                <strong>{Number(zScoreVisualizationSettings.threshold ?? 2).toFixed(1)}</strong>
                            </div>
                            <input
                                type="range"
                                min="0"
                                max="6"
                                step="0.1"
                                value={zScoreVisualizationSettings.threshold ?? 2}
                                disabled={!zScoreVisualizationSettings.feature || !zScoreVisualizationSettings.thresholdMode}
                                onChange={(e) => setZScoreVisualizationSettings(prev => ({
                                    ...prev,
                                    threshold: Number(e.target.value)
                                }))}
                            />
                        </div>
                    </div>

                    <div className="control-row">
                        <label className="checkbox-label">
                            <input
                                type="checkbox"
                                checked={Boolean(zScoreVisualizationSettings.showClinicalOverlay)}
                                disabled={selectedPatients.length === 0}
                                onChange={(e) => setZScoreVisualizationSettings(prev => ({
                                    ...prev,
                                    showClinicalOverlay: e.target.checked
                                }))}
                            />
                            <span>Overlay clinical SOZ metadata</span>
                        </label>
                    </div>
                </div>
            </section>

            <section className="control-section">
                <div className="normative-panel-controls-footer">
                    <button
                        className="analyze-button"
                        disabled={selectedPatients.length === 0 || !zScoreVisualizationSettings.feature || zScoresLoading}
                        onClick={() => handleGenerateZScores({ showButtonLoading: true, force: true })}
                    >
                        <BarChart2 size={14} />
                        <span>{zScoresLoading ? 'Plotting...' : 'Plot Z-Score Points'}</span>
                    </button>

                    {(selectedPatients.length === 0 || !zScoreVisualizationSettings.feature) && (
                        <div className="helper-message" style={{ fontSize: '12px', color: 'rgba(255, 255, 255, 0.6)' }}>
                            {selectedPatients.length === 0
                                ? 'Select patient(s) first'
                                : 'Select a feature for visualization'}
                        </div>
                    )}
                </div>

                <div className="normative-panel-download-row">
                    <button
                        className="download-button"
                        disabled={selectedPatients.length === 0}
                        onClick={handleDownloadSelections}
                    >
                        <Download size={14} />
                        <span>Download JSON</span>
                    </button>
                    <button
                        className="download-button clinical-report-button"
                        disabled={selectedPatients.length === 0 || !zScoreVisualizationSettings.feature}
                        onClick={handleDownloadClinicalReport}
                    >
                        <Download size={14} />
                        <span>Clinical Report</span>
                    </button>
                </div>
            </section>
        </>
    );
};
